import { defineModule } from '../utils/helpers';

/**
 * Accordion behaviour for every FAQ list on the page — the `faq` block as well
 * as the per-category lists on the overview page. One group is one set of
 * mutually exclusive items: opening a question closes the one that was open.
 *
 * The open state lives in the `accordion--open` class, so a template can render
 * an item expanded server side without any help from here. Clicks are handled on
 * the group itself, which keeps working when the overview page filters items in
 * and out of the list.
 */
const setupGroup = (group: HTMLElement): (() => void) => {
  const setOpen = (item: HTMLElement, open: boolean): void => {
    const content = item.querySelector('.accordion__content');

    item.classList.toggle('accordion--open', open);
    item
      .querySelector('.accordion__trigger')
      ?.setAttribute('aria-expanded', open.toString());
    content?.setAttribute('aria-hidden', (!open).toString());
    content?.toggleAttribute('inert', !open);
  };

  const onClick = (event: Event): void => {
    const trigger = (event.target as HTMLElement).closest<HTMLElement>(
      '.accordion__trigger',
    );
    if (!trigger) return;

    const clicked = trigger.closest<HTMLElement>('.accordion');
    if (!clicked) return;

    const shouldOpen = !clicked.classList.contains('accordion--open');

    group
      .querySelectorAll<HTMLElement>('.accordion')
      .forEach((item) => setOpen(item, item === clicked && shouldOpen));
  };

  group.addEventListener('click', onClick);

  return () => group.removeEventListener('click', onClick);
};

let cleanups: Array<() => void> = [];

export default defineModule(
  () => {
    cleanups = Array.from(
      document.querySelectorAll<HTMLElement>('[data-accordion-group]'),
    ).map(setupGroup);
  },
  () => {
    cleanups.forEach((fn) => fn());
    cleanups = [];
  },
);
